import React from "react";
import classNames from "classnames";
import { GridRow } from "@design-system/Grid/GridRow";
import { GridCol } from "@design-system/Grid/GridCol";
import styles from "./DescriptionList.module.scss";

interface DescriptionListProps {
    children: React.ReactNode;
    className?: string;
}

/** Vertical list of label/value items. */
export const DescriptionList = ({
    children,
    className,
}: DescriptionListProps) => (
    <div className={classNames(styles.list, className)}>{children}</div>
);

interface DescriptionListItemProps {
    label: React.ReactNode;
    children: React.ReactNode;
    /**
     * "row": label and value share a 1fr/2fr grid row (form drawers).
     * "stack": label above value (document metadata).
     * "columns": 4/8 grid columns with fixed row height (details panels).
     */
    layout?: "row" | "stack" | "columns";
    /** Row layout: align the label to the top for multi-line values. */
    alignTop?: boolean;
    /** Row layout: renders the label as <label htmlFor>. */
    htmlFor?: string;
    /** Columns layout: replaces the default value styling for content that owns its own layout. */
    valueClassName?: string;
    /** Stack layout: data-testid applied to the value node. */
    valueTestId?: string;
}

export const DescriptionListItem = ({
    label,
    children,
    layout = "row",
    alignTop,
    htmlFor,
    valueClassName,
    valueTestId,
}: DescriptionListItemProps) => {
    if (layout === "stack") {
        return (
            <div className={styles.stackItem}>
                <div className={styles.stackLabel}>{label}</div>
                <div className={styles.stackValue} data-testid={valueTestId}>
                    {children}
                </div>
            </div>
        );
    }

    if (layout === "columns") {
        return (
            <GridRow gap="0">
                <GridCol sm={4} className={styles.columnsLabel}>
                    {label}
                </GridCol>
                <GridCol
                    sm={8}
                    className={valueClassName ?? styles.columnsValue}
                >
                    {children}
                </GridCol>
            </GridRow>
        );
    }

    return (
        <div
            className={classNames(styles.row, {
                [styles.rowTop]: alignTop,
            })}
        >
            {htmlFor ? (
                <label htmlFor={htmlFor} className={styles.rowLabel}>
                    {label}
                </label>
            ) : (
                <span className={styles.rowLabel}>{label}</span>
            )}
            {children}
        </div>
    );
};
